import { useCallback, useEffect, useState } from 'react';
import api from '../api/axiosConfig';

// The account's auto tagging setting, which the composer's switch reads and
// writes. It lives on the server so turning it off on one device keeps it
// off on every other. Cached at module scope like usePremium, so opening the
// composer doesn't refetch it each time. On until known, matching the
// server's default.

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

const publish = (v: boolean) => {
    cached = v;
    listeners.forEach((l) => l(v));
};

const fetchAutoTagging = (): Promise<boolean> => {
    if (inflight) return inflight;
    inflight = api.get('/user/settings/')
        .then((res) => {
            publish(res.data.auto_tagging !== false);
            return cached!;
        })
        // Leave it unknown so the next composer asks again
        .catch(() => true)
        .finally(() => { inflight = null; });
    return inflight;
};

export function useAutoTagging(): [boolean, (on: boolean) => void] {
    const [autoTagging, setLocal] = useState<boolean>(cached ?? true);

    useEffect(() => {
        let active = true;
        const update = (v: boolean) => { if (active) setLocal(v); };
        listeners.add(update);
        if (cached !== null) {
            update(cached);
        } else {
            fetchAutoTagging().then(update);
        }
        return () => { active = false; listeners.delete(update); };
    }, []);

    const setAutoTagging = useCallback((on: boolean) => {
        const previous = cached ?? true;
        publish(on);
        // Flipped back if the save fails, so the switch never shows a
        // setting the account doesn't have
        api.patch('/user/settings/', { auto_tagging: on }).catch(() => publish(previous));
    }, []);

    return [autoTagging, setAutoTagging];
}
